<script>
import {ref, onMounted, onBeforeUnmount} from "vue";
import {IMaskDirective} from 'vue-imask';
import {useSwipe} from '@vueuse/core';
import IconX from "@js/components/Icons/IconX.vue";
import CallbackErrorMessage from "@js/components/Statuses/CallbackErrorMessage.vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import {useCallbackStore} from "@js/store/CallbackStore.js";
import PrivacyPolicyAgreementLink from "@js/components/Sections/Links/PrivacyPolicyAgreementLink.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import IconAnimatedCheckMark from "@js/components/Icons/IconAnimatedCheckMark.vue";

export default {
    name: "BaseCallback",
    components: {
        IconAnimatedCheckMark,
        OnlyMobile,
        PrivacyPolicyAgreementLink,
        SpinnerIndicator,
        CallbackErrorMessage,
        IconX,
    },
    directives: {
        imask: IMaskDirective
    },
    data() {
        return {
            phoneMask: {
                mask: '+7 (000) 000-00-00',
            },
        }
    },
    methods: {
        updatePhone(newPhone) {
            this.callbackStore.base_form.data.phone = newPhone;
        },
        triggerFileInput() {
            this.$refs.fileInput?.click()
        },
        onFilesChange(event) {
            const files = Array.from(event.target.files || [])
            if (!files.length) return

            this.callbackStore.addBaseFormFiles(files)
            event.target.value = ''
        },
        removeFile(index) {
            this.callbackStore.removeBaseFormFile(index)
        },
        isImage(file) {
            return file?.type?.startsWith('image/')
        },
        formatFileSize(bytes) {
            if (!bytes && bytes !== 0) return ''
            if (bytes < 1024) return `${bytes} Б`
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`
            return `${(bytes / 1024 / 1024).toFixed(1)} МБ`
        }
    },
    setup() {
        const callbackStore = useCallbackStore()
        const modalRef = ref(null)
        const contentRef = ref(null)
        const submitRef = ref(null)
        const showFloatingButton = ref(false)

        const closeModal = () => {
            callbackStore.closeCallbackPopup()
        }

        const handleBackgroundClick = (event) => {
            if (!modalRef.value?.contains(event.target)) {
                closeModal()
            }
        }

        const canCloseBySwipe = () => {
            if (typeof window === 'undefined') return false
            if (window.innerWidth >= 1024) return false
            if (!contentRef.value) return true
            return contentRef.value.scrollTop <= 0
        }

        const handleScroll = () => {
            if (
                !submitRef.value ||
                !contentRef.value ||
                !callbackStore.base_form.isOpen ||
                callbackStore.base_form.successfully_sent
            ) {
                showFloatingButton.value = false
                return
            }

            const submitRect = submitRef.value.getBoundingClientRect()
            const contentRect = contentRef.value.getBoundingClientRect()

            showFloatingButton.value = submitRect.bottom > contentRect.bottom
        }

        useSwipe(modalRef, {
            threshold: 80,
            passive: true,
            onSwipeEnd(_, direction) {
                if (!canCloseBySwipe()) return

                if (direction === 'down') {
                    closeModal()
                }
            }
        })

        onMounted(() => {
            setTimeout(handleScroll, 100)
        })

        onBeforeUnmount(() => {
            callbackStore.clearBaseFormFilePreviews()
        })

        return {
            callbackStore,
            modalRef,
            contentRef,
            submitRef,
            showFloatingButton,
            closeModal,
            handleBackgroundClick,
            handleScroll,
        }
    },
    updated() {
        this.$nextTick(() => {
            this.handleScroll()
        })
    }
}
</script>

<template>
    <div v-if="callbackStore.base_form.isOpen"
         class="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/70 transition-opacity"
         @click="handleBackgroundClick">

        <div ref="modalRef"
             @click.stop
             :class="{'top-i-0': callbackStore.base_form.isOpen && callbackStore.base_form.animation_in_progress}"
             class="relative top-[100%] w-full h-full lg:w-full lg:max-w-[740px] min-h-[625px] 2xl:h-[65vh] bg-white rounded-tl-2xl rounded-tr-2xl lg:rounded-[32px] shadow-2xl transition-all duration-500 overflow-hidden flex flex-col">

            <button @click.stop.prevent="closeModal"
                    class="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center md:hover:bg-gray-100 focus:outline-none transition">
                <IconX class="h-6 w-6 stroke-[#000] active:stroke-baseColor md:hover:stroke-baseColor transition"/>
            </button>

            <OnlyMobile :maxSize="1024" class="absolute top-3 right-0 left-0 m-auto flex justify-center z-10">
                <div class="h-[5px] w-[50px] bg-gray-400 rounded-full"></div>
            </OnlyMobile>

            <div ref="contentRef"
                 class="flex-1 min-h-0 overflow-y-auto custom-scroll pb-24"
                 @scroll="handleScroll">
                <div v-if="!callbackStore.base_form.successfully_sent"
                     class="w-full max-w-[680px] mx-auto px-4 pt-14 pb-8 md:px-8 md:pt-8 md:pb-8">

                    <div class="mb-6 md:mb-7">
                        <p class="text-[30px] md:text-[42px] font-bold text-[#2D2D2D] leading-none">
                            {{ callbackStore.base_form.title }}
                        </p>
                        <p class="mt-2 text-sm text-gray-500">
                            Оставьте контакты и прикрепите фото для предварительной оценки
                        </p>
                    </div>

                    <div class="space-y-4">
                        <div>
                            <div class="relative">
                                <input type="text"
                                       id="ptf_phone"
                                       @keyup="updatePhone($event.target.value)"
                                       :value="callbackStore.base_form.data.phone"
                                       v-imask="phoneMask"
                                       class="block px-3 pb-2.5 pt-4 w-full text-[15px] md:text-[16px] text-gray-900 bg-transparent rounded-2xl border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-baseColor peer"
                                       :class="[callbackStore.base_form.errors && callbackStore.base_form.errors['phone'] ? 'border-red-500' : '']"
                                       placeholder=" "
                                       required/>
                                <label for="ptf_phone"
                                       class="absolute text-[13px] text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-baseColor peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 start-2">
                                    Номер телефона
                                </label>
                            </div>
                            <CallbackErrorMessage :error="callbackStore.base_form.errors" :error-msg="'phone'"/>
                        </div>

                        <div>
                            <div class="relative">
                                <input type="text"
                                       id="ptf_full_name"
                                       v-model="callbackStore.base_form.data.full_name"
                                       class="block px-3 pb-2.5 pt-4 w-full text-[15px] md:text-[16px] text-gray-900 bg-transparent rounded-2xl border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-baseColor peer"
                                       :class="[callbackStore.base_form.errors && callbackStore.base_form.errors['full_name'] ? 'border-red-500' : '']"
                                       placeholder=" "
                                       required/>
                                <label for="ptf_full_name"
                                       class="absolute text-[13px] text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-baseColor peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 start-2">
                                    Как к вам обращаться?
                                </label>
                            </div>
                            <CallbackErrorMessage :error="callbackStore.base_form.errors" :error-msg="'full_name'"/>
                        </div>

                        <div class="rounded-[22px] border border-gray-200 bg-[#fcfcfc] p-4">
                            <div class="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <p class="text-[15px] md:text-[17px] font-semibold text-[#2D2D2D]">
                                        Прикрепить фото
                                    </p>
                                    <p class="text-xs text-gray-500 mt-1">
                                        До 10 фото. JPG, PNG, WEBP, HEIC, HEIF. Не более 10 МБ каждое
                                    </p>
                                </div>
                                <span class="hidden md:inline-flex text-xs text-gray-400 whitespace-nowrap">
                                    до 10 фото
                                </span>
                            </div>

                            <input ref="fileInput"
                                   type="file"
                                   class="hidden"
                                   multiple
                                   accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif"
                                   @change="onFilesChange"/>

                            <button type="button"
                                    @click.stop="triggerFileInput"
                                    class="w-full rounded-[20px] border border-dashed border-gray-300 bg-white px-4 py-4 text-left transition md:hover:border-baseColor md:hover:bg-[#fff7eb]">
                                <div class="flex items-center justify-between gap-4">
                                    <div>
                                        <p class="font-semibold text-[#2D2D2D] text-[15px]">
                                            Загрузить фото
                                        </p>
                                        <p class="text-[13px] text-gray-500 mt-1">
                                            Нажмите, чтобы выбрать одно или несколько фото
                                        </p>
                                    </div>
                                    <div class="shrink-0 rounded-full bg-baseColor px-4 py-2 text-sm font-semibold text-black">
                                        Выбрать
                                    </div>
                                </div>
                            </button>

                            <CallbackErrorMessage :error="callbackStore.base_form.errors" :error-msg="'files'"/>

                            <div v-if="callbackStore.base_form.files.length" class="mt-4">
                                <div class="flex items-center justify-between gap-3 mb-3">
                                    <p class="text-sm font-medium text-[#2D2D2D]">
                                        Загруженные фото
                                    </p>
                                    <span class="text-xs text-gray-400">
                                        {{ callbackStore.base_form.files.length }} шт.
                                    </span>
                                </div>

                                <div class="max-h-[300px] overflow-y-auto pr-1 custom-scroll">
                                    <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        <div v-for="(file, index) in callbackStore.base_form.files"
                                             :key="`${file.name}-${file.size}-${index}`"
                                             class="relative rounded-[18px] border border-gray-200 bg-white overflow-hidden shadow-sm">
                                            <button type="button"
                                                    @click.stop="removeFile(index)"
                                                    class="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center shadow md:hover:bg-red-50 transition">
                                                <IconX class="w-4 h-4 stroke-[#000] md:hover:stroke-red-500 transition"/>
                                            </button>

                                            <img :src="callbackStore.base_form.file_previews[index]"
                                                 :alt="file.name"
                                                 class="w-full h-[120px] object-cover"/>

                                            <div class="p-3">
                                                <p class="text-sm font-medium text-[#2D2D2D] line-clamp-2 break-all">
                                                    {{ file.name }}
                                                </p>
                                                <p class="text-xs text-gray-500 mt-1">
                                                    {{ formatFileSize(file.size) }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div ref="submitRef" class="pt-1">
                            <button @click.stop="callbackStore.sendPopupForm"
                                    :disabled="callbackStore.base_form.sending"
                                    class="flex justify-center items-center w-full text-black border-2 border-baseColor bg-baseColor md:hover:bg-transparent font-semibold rounded-full text-base px-10 py-3.5 focus:outline-none transition cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed">
                                <span v-if="!callbackStore.base_form.sending">
                                    Отправить заявку
                                </span>
                                <span v-else>
                                    Отправляем
                                </span>
                                <SpinnerIndicator v-if="callbackStore.base_form.sending" class="w-5 h-5 ms-2"/>
                            </button>
                        </div>

                        <div class="pt-0.5">
                            <PrivacyPolicyAgreementLink/>
                        </div>
                    </div>
                </div>

                <div v-else class="w-full max-w-[520px] mx-auto px-6 py-16 md:py-20 flex flex-col items-center justify-center">
                    <IconAnimatedCheckMark size="80"/>
                    <p class="text-xl md:text-2xl font-medium text-center text-[#7ac142] mt-5">
                        Спасибо!<br/> Мы скоро свяжемся с вами.
                    </p>
                </div>
            </div>

            <transition name="fade-up">
                <div v-if="showFloatingButton && !callbackStore.base_form.successfully_sent"
                     class="absolute bottom-0 left-0 right-0 z-20 p-3 bg-gradient-to-t from-white via-white to-white/90 border-t border-gray-200">
                    <button
                        @click.stop="callbackStore.sendPopupForm"
                        :disabled="callbackStore.base_form.sending"
                        class="w-full flex justify-center items-center text-black border-2 border-baseColor bg-baseColor md:hover:bg-transparent font-semibold rounded-full text-base px-10 py-3.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition disabled:opacity-70"
                    >
                        <span v-if="!callbackStore.base_form.sending">
                            Отправить заявку
                        </span>
                        <span v-else>
                            Отправляем
                        </span>
                        <SpinnerIndicator v-if="callbackStore.base_form.sending" class="w-5 h-5 ms-2"/>
                    </button>
                </div>
            </transition>
        </div>
    </div>
</template>

<style scoped lang="scss">
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.custom-scroll {
    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;
}

.custom-scroll::-webkit-scrollbar {
    width: 8px;
}

.custom-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scroll::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 999px;
}

.custom-scroll::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}

.fade-up-enter-active,
.fade-up-leave-active {
    transition: all .25s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
    opacity: 0;
    transform: translateY(15px);
}
</style>
